import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { MessageCircle, X, Send, Loader2, ThumbsUp, ThumbsDown, Heart } from 'lucide-react';

/**
 * TittiChatWidget – flytende chat-knapp + panel tilgjengelig på alle sider
 * for innloggede brukere. Bruker /api/titti/chat.
 *
 * Lagrer session_id i localStorage så samtalen er kontinuerlig
 * mellom side-navigeringer (men ikke mellom innlogginger).
 */
export default function TittiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  // Hent session-id og lagre historikk lokalt for fortsetting
  useEffect(() => {
    const stored = localStorage.getItem('titti_session_id');
    if (stored) setSessionId(stored);

    const storedMsgs = localStorage.getItem('titti_messages');
    if (storedMsgs) {
      try {
        setMessages(JSON.parse(storedMsgs));
      } catch {
        // ignore parse errors
      }
    }
  }, []);

  // Auto-scroll til bunn ved nye meldinger
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Persistér chat lokalt (siste 30 meldinger)
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('titti_messages', JSON.stringify(messages.slice(-30)));
    }
  }, [messages]);

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg, ts: Date.now() }]);
    setLoading(true);

    try {
      const resp = await api.post('/titti/chat', {
        message: msg,
        session_id: sessionId,
      });
      const data = resp.data;
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem('titti_session_id', data.session_id);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'titti',
          content: data.reply,
          matched_faq: data.matched_faq,
          data_used: data.data_used,
          ts: Date.now(),
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'titti',
          content: `Beklager, jeg klarte ikke å svare nå 💔 (${err.response?.data?.detail || err.message})`,
          error: true,
          ts: Date.now(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('titti_session_id');
    localStorage.removeItem('titti_messages');
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="titti-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white rounded-full p-4 shadow-lg hover:shadow-2xl transition-all duration-200 hover:scale-105 group"
          aria-label="Chat med Titti"
        >
          <div className="relative">
            <MessageCircle size={26} />
            <span className="absolute -top-1 -right-1 bg-white text-rose-500 rounded-full text-[10px] font-bold w-5 h-5 flex items-center justify-center shadow">
              💛
            </span>
          </div>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-800 text-white text-xs rounded px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Spør Titti
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="titti-panel"
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-rose-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-500 to-amber-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-white text-rose-500 rounded-full w-9 h-9 flex items-center justify-center font-bold text-sm shadow">
                T
              </div>
              <div>
                <div className="font-semibold text-sm flex items-center gap-1">
                  Titti <Heart size={12} fill="currentColor" />
                </div>
                <div className="text-[11px] opacity-90">Hjelper deg gjerne</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  data-testid="titti-clear"
                  onClick={clearChat}
                  className="text-[10px] bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition"
                  title="Tøm samtale"
                >
                  Ny samtale
                </button>
              )}
              <button
                data-testid="titti-close"
                onClick={() => setOpen(false)}
                className="hover:bg-white/20 rounded p-1 transition"
                aria-label="Lukk"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-rose-50/30 to-white">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8 px-4">
                <div className="text-4xl mb-3">🧶</div>
                <p className="text-sm font-medium text-gray-700">Hei! Jeg er Titti 💛</p>
                <p className="text-xs mt-2 text-gray-500">
                  Spør meg om varetelling, leverandører, oppskrifter eller<br/>
                  bare hva du lurer på.
                </p>
                <div className="mt-4 flex flex-col gap-1.5 items-center">
                  <button
                    onClick={() => setInput('Hvem er du Titti?')}
                    className="text-xs bg-rose-100 hover:bg-rose-200 text-rose-700 px-3 py-1.5 rounded-full transition"
                  >
                    Hvem er du Titti?
                  </button>
                  <button
                    onClick={() => setInput('Hvordan starter jeg varetelling?')}
                    className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-700 px-3 py-1.5 rounded-full transition"
                  >
                    Hvordan starter jeg varetelling?
                  </button>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-rose-500 text-white rounded-br-sm'
                      : m.error
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                  {m.matched_faq && (
                    <div className="text-[10px] mt-1 opacity-60 italic">
                      ✨ Forhåndsdefinert svar
                    </div>
                  )}
                  {m.data_used && m.data_used.length > 0 && (
                    <div className="text-[10px] mt-2 pt-2 border-t border-gray-300/40 opacity-80">
                      📊 Data hentet:
                      <ul className="mt-1 ml-3 space-y-0.5">
                        {m.data_used.map((d, di) => (
                          <li key={di} className="font-mono">• {d.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 size={14} className="animate-spin" />
                  Titti tenker...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-rose-100 p-3 bg-white">
            <div className="flex gap-2 items-end">
              <textarea
                data-testid="titti-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Spør Titti om noe..."
                rows={1}
                className="flex-1 resize-none border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent max-h-24"
                disabled={loading}
              />
              <button
                data-testid="titti-send"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-rose-500 hover:bg-rose-600 disabled:bg-gray-300 text-white rounded-xl p-2 transition"
                aria-label="Send"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
